import { useState } from 'react'
import { Icon } from '@iconify/react'
import { Formik, Form } from 'formik'
import {
  Button,
  Checkbox,
  FormikSelectField,
  FormikTextField,
  Tooltip,
  useMessage,
  useCreateBulkTopics,
} from '@pepper/ui'
import { Topic } from '@pepper/utils/src/types/seo'

import { useGenerateBulkSeoGuidelines } from 'utils/hooks/useGenerateBulkSeoGuidelines'
import { type FormValues } from './types'
import addTopicSchema from './schema'

import s from './ManualForm.module.css'

type Props = {
  companyId: string
  projectId: string
  onClose: () => void
  domainRegion: string
  setIsAddingBulkTopics: (value: boolean) => void
}

export const ManualForm = ({
  onClose,
  companyId,
  projectId,
  domainRegion,
  setIsAddingBulkTopics,
}: Props) => {
  const [shouldAttachSeoGuidelines, setShouldAttachSeoGuidelines] =
    useState(true)
  const { showError, showSuccess } = useMessage()
  const { mutateAsync: addBulkTopics, isLoading: isBulkAdditionLoading } =
    useCreateBulkTopics()
  const { mutateAsync: generateBulkSeoGuidelines } =
    useGenerateBulkSeoGuidelines()

  const initialValues = {
    name: '',
    primaryKeyword: '',
    secondaryKeywords: [],
  }

  const handleAddTopic = async (values: FormValues) => {
    setIsAddingBulkTopics(true)

    const response = await addBulkTopics({
      companyId,
      projectId,
      data: [
        {
          topic: values.name,
          primary_keyword: values.primaryKeyword,
          secondary_keywords: values.secondaryKeywords.map(keyword =>
            keyword.trim()
          ),
        },
      ],
    })

    const records = response?.records
    const newRecordIds = response?.new_record_ids

    const generatedTopics =
      records?.filter((record: Topic) => {
        return newRecordIds?.includes(record.id)
      }) ?? []

    if (response?.status === 'success') {
      if (newRecordIds?.length) {
        if (shouldAttachSeoGuidelines && !!values.primaryKeyword) {
          const seoGuidelineResponse = await generateBulkSeoGuidelines({
            companyId,
            topics: generatedTopics.map(topic => ({
              topic_id: topic.id,
              topic: topic.name,
              region_code: domainRegion,
            })),
          })

          if (seoGuidelineResponse.status === 'error') {
            showError(seoGuidelineResponse.error)
          }
        }
        showSuccess('Topic added successfully')
      } else {
        showSuccess('Successfully updated topics')
      }
    } else {
      showError(response?.description)
    }
    setIsAddingBulkTopics(false)
    onClose()
  }

  return (
    <div className={s.wrapper}>
      <Formik
        initialValues={initialValues}
        validationSchema={addTopicSchema}
        enableReinitialize={true}
        onSubmit={handleAddTopic}
      >
        {({ dirty, isValid, values }): React.ReactNode => {
          const disableSEOGuideline = !values.primaryKeyword

          return (
            <Form className={s.wrapper}>
              <FormikTextField
                label="Topic"
                name="name"
                required={true}
                fieldProps={{
                  required: true,
                  placeholder: 'Topic',
                }}
              />
              <FormikTextField
                name="primaryKeyword"
                className={s.select}
                label={<span className={s.label}>Primary Keyword&nbsp;</span>}
                fieldProps={{
                  placeholder: 'Enter Primary Keyword',
                }}
              />
              <FormikSelectField
                name="secondaryKeywords"
                className={s.select}
                label={
                  <span className={s.label}>Secondary Keywords&nbsp;</span>
                }
                fieldProps={{
                  mode: 'tags',
                  placeholder: 'Enter Secondary Keywords',
                  helperText: 'Add multiple keywords separated by comma',
                  tokenSeparators: [','],
                  open: false,
                  textFormat: (value: string[]) => {
                    return value.filter(v => v.trim())
                  },
                  onKeyDown: e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                    }
                  },
                }}
              />

              <div className={s.seoGuidelines}>
                <Checkbox
                  checked={shouldAttachSeoGuidelines}
                  onChange={() =>
                    setShouldAttachSeoGuidelines(prevVal => !prevVal)
                  }
                  disabled={disableSEOGuideline}
                >
                  Create SEO Content Guidelines for Topic
                </Checkbox>
                <Tooltip
                  placement="bottom"
                  title={`Guidelines include best practices in terms of SEO, readability and
          more from top ranking pages. ${
            disableSEOGuideline
              ? 'Please add a primary Keyword to enable this option.'
              : ''
          }`}
                >
                  <Icon icon="lucide:info" width={14} height={14} />
                </Tooltip>
              </div>

              <div className={s.footer}>
                <Button type="link" htmlType="button" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isBulkAdditionLoading}
                  disabled={!dirty || !isValid}
                >
                  Add Topic
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
